import { useMemo } from 'react';
import {
  sortBySeverity,
  getSeverityLabel,
} from '@kirin/api-client/helpers/severity';
import { Severity } from '@kirin/api-client/contracts/shared/severity';

/**
 * Item with severity for sorting purposes
 */
interface ItemWithSeverity {
  severity?: string;
  [key: string]: unknown;
}

/**
 * Sorted item with original index preserved for deletion
 */
export interface SortedItemWithIndex<T> {
  item: T;
  originalIndex: number;
}

/**
 * Sort an array of items by severity while preserving original indices.
 * This is useful for displaying items in severity order while maintaining
 * the ability to delete items by their original index.
 *
 * @param items - Array of items to sort
 * @param getSeverity - Function to extract severity from an item (defaults to item.severity)
 * @returns Sorted array with original indices preserved
 */
export function useSortedItemsWithIndices<T extends ItemWithSeverity>(
  items: T[],
  getSeverity?: (item: T) => string | undefined
): SortedItemWithIndex<T>[] {
  return useMemo(() => {
    const indexed = items.map((item, index) => ({
      item,
      originalIndex: index,
      severity: getSeverity ? getSeverity(item) : item.severity,
    }));
    return sortBySeverity(indexed);
  }, [items, getSeverity]);
}

/**
 * Remove an item from an array by its original index.
 * Useful when items are sorted but deletion needs to happen by original position.
 *
 * @param items - Original array
 * @param originalIndex - Index in the original (unsorted) array
 * @returns New array with the item removed
 */
export function removeItemByIndex<T>(items: T[], originalIndex: number): T[] {
  return items.filter((_, index) => index !== originalIndex);
}

/**
 * Severity options for select dropdowns.
 * Derived from Severity enum and getSeverityLabel helper to ensure consistency.
 */
export const SEVERITY_OPTIONS = Object.values(Severity).map(level => ({
  value: level,
  label: getSeverityLabel(level),
})) as Array<{ value: Severity; label: string }>;
